import { useState } from 'react'
import type { Block, ExtractBlock } from '@w/content-blocks'
import { ChevronRightIcon } from '@w/ui/components/icon'
import { findBlock, plain, rows, pointAt } from '../../core/tree-read'
import type { CoreViewApi } from '../view-api'

/** Disclosure is view-local: authored content and its history remain untouched. */
export function ToggleList({ block, api, draggable }: { draggable?: boolean; block: ExtractBlock<'list'>; api: CoreViewApi }) {
  const { renderBlock } = api
  return <ul data-block-id={block.id} draggable={draggable} className="my-1 list-none p-0">
    {block.children.map(item => item.type === 'li'
      ? <ToggleItem key={item.id} item={item} api={api} /> : renderBlock(item))}
  </ul>
}

function ToggleItem({ item, api }: { item: ExtractBlock<'li'>; api: CoreViewApi }) {
  // A toggle with nothing to hide starts closed: Enter after a closed toggle adds "a new empty, closed toggle"
  // (notion-browser-queue.md 40); one with a body starts open.
  const [open, setOpen] = useState(item.children.length > 1)
  const [lastReveal, setLastReveal] = useState(api.revealRequests)
  const { renderBlock } = api
  const [header, ...body] = item.children
  if (lastReveal !== api.revealRequests) {
    setLastReveal(api.revealRequests)
    if (api.revealRequests?.some(request => request.toggleItems.has(item.id))) setOpen(true)
  }
  const selection = api.state.selection
  const selectedBody = api.state.scope ? findBlock(body, api.state.scope.blockId)
    : selection && (findBlock(body, selection.anchor.blockId) ?? findBlock(body, selection.focus.blockId))
  // Programmatic navigation and arrow motion must never leave the caret in hidden content.
  if (!open && selectedBody) setOpen(true)
  function toggle() {
    if (open && selectedBody && header) {
      const row = rows([header]).at(-1)
      if (row) {
        const point = pointAt(row, plain(row.block).length)
        api.select({ anchor: point, focus: point })
      }
    }
    setOpen(value => !value)
  }
  return <li data-block-id={item.id} className="list-none">
    <div className="flex items-start gap-1">
      <button type="button" contentEditable={false} data-testid={`toggle-${item.id}`} data-toggle-control={item.id}
        aria-label={`${open ? 'Close' : 'Open'} toggle${header ? `: ${plain(header)}` : ''}`}
        aria-expanded={open} aria-controls={`toggle-body-${item.id}`}
        className="text-on-surface-secondary hover:bg-surface-2 flex size-7 shrink-0 items-center justify-center rounded focus-visible:outline focus-visible:outline-2"
        onKeyDownCapture={event => { if (event.key === 'Enter' || event.key === ' ') event.stopPropagation() }}
        onPointerDown={event => { event.preventDefault() }} onClick={toggle}>
        <ChevronRightIcon className={`size-4 ${open ? 'rotate-90' : ''}`} />
      </button>
      <div className="min-w-0 flex-1 [&>.cb-paragraph]:my-0">{header ? renderBlock(header) : <br />}</div>
    </div>
    <div id={`toggle-body-${item.id}`} data-toggle-body={item.id} data-testid={`toggle-body-${item.id}`} hidden={!open} className="ps-8">
      {body.map((child: Block) => renderBlock(child))}
    </div>
  </li>
}
